import { packageColours } from './palette';
import { CommandPackage, CostBasis } from './types';

export function packageLabel(row: CommandPackage): string {
  if (row.type === 'cancellation' && row.name === 'Kept from cancellations') return 'Insurance Breakage';
  return row.type === 'base'
    ? row.name
    : row.name.replace(/^\s*(Limited Time Offer:|Extended by Popular Demand:)\s*/i, '').trim();
}

export function packageBreakdown(packages: CommandPackage[]) {
  const ltleCount = packages.filter((row) => row.type !== 'base' && row.type !== 'cancellation').length;
  let ltleIndex = 0;
  return packages.map((row) => {
    const isLtle = row.type !== 'base' && row.type !== 'cancellation';
    const index = isLtle ? ltleIndex++ : 0;
    return {
      row,
      colour: isLtle ? packageColours.ltle[index % packageColours.ltle.length] : packageColours.base,
      legendLabel: isLtle ? `LTLE${ltleCount > 1 ? ` ${String.fromCharCode(65 + index)}` : ''}` : 'Tour only',
      excludedPax: row.type === 'cancellation' ? 0 : Math.max(0, row.pax - row.margin_pax),
    };
  });
}

export function packageExclusionNotes(row: CommandPackage): string[] {
  return [
    [row.bookings_no_hotel_order, 'no hotel order yet'],
    [row.bookings_addon_in_tour, 'add-on supplied inside the tour booking'],
    [row.bookings_no_tour_cost, 'no recorded cost'],
  ]
    .filter(([count]) => Number(count) > 0)
    .map(([count, reason]) => `${count} booking${count === 1 ? '' : 's'} not in margin: ${reason}`);
}

export function packageMoney(row: CommandPackage, basis: CostBasis) {
  if (row.type !== 'cancellation' && row.margin_pax === 0) return null;
  const tourCost = basis === 'rep' ? row.cost_sales : row.tour_cost;
  const addonCost = basis === 'rep' ? 0 : row.hotel_cost + row.other_addon_cost;
  const margin = row.type === 'cancellation' ? row.margin : row.margin_ttv - tourCost - row.ics_cost - addonCost;
  return {
    ttv: row.margin_ttv,
    tourCost: tourCost + row.ics_cost,
    addonCost,
    margin,
    marginPct: row.margin_ttv > 0 && margin != null ? margin / row.margin_ttv : null,
  };
}

/** Keep types separate even if an upstream package identifier is reused. */
export function aggregateTourPackages(departures: import('./types').CommandDeparture[]) {
  const groups = new Map<string, CommandPackage & { departures: number }>();
  const fields = [
    'bookings',
    'pax',
    'ttv',
    'margin_pax',
    'margin_ttv',
    'cost_sales',
    'tour_cost',
    'ics_cost',
    'hotel_cost',
    'other_addon_cost',
    'bookings_no_hotel_order',
    'bookings_addon_in_tour',
    'bookings_no_tour_cost',
  ] as const;
  for (const departure of departures) {
    const seen = new Set<string>();
    for (const row of departure.packages ?? []) {
      const key = `${row.type}:${row.package_opp_id || row.name}`;
      let group = groups.get(key);
      if (!group) {
        group = { ...row, departures: 0, margin: null };
        fields.forEach((field) => {
          group[field] = 0;
        });
        groups.set(key, group);
      }
      fields.forEach((field) => {
        group[field] += row[field] ?? 0;
      });
      if (row.margin != null) group.margin = (group.margin ?? 0) + row.margin;
      if (!seen.has(key)) group.departures++;
      seen.add(key);
    }
  }
  return [...groups.values()].sort((a, b) => {
    const rank = (row: CommandPackage) => (row.type === 'base' ? 0 : row.type === 'cancellation' ? 2 : 1);
    return rank(a) - rank(b);
  });
}
